/**
 * User Signup Service
 * Handles all signup-related API calls
 */

import { API_ENDPOINTS, apiCall } from '../../config/apiConfig';
import SignupRequestStructure from '../../Pages/PublicLayout/Signuppage/constants/userSignupRequest';
import SignupResponseStructure from '../../Pages/PublicLayout/Signuppage/constants/UserSignupResponse';

/**
 * Sign up a new user
 * @param {Object} userData - User signup data
 * @returns {Promise<Object>} Signup response
 */
export const signupUser = async (userData) => {
  try {
    console.log('📤 signupUser called with:', userData);

    // ✅ Ensure userData follows the structure
    const requestData = { ...SignupRequestStructure, ...userData };

    console.log('📦 Prepared request data:', requestData);
    
    // ✅ Use apiCall from apiConfig instead of direct fetch
    const result = await apiCall(API_ENDPOINTS.AUTH.SIGNUP, {
      method: 'POST',
      body: JSON.stringify(requestData),
    });

    if (result.success) {
      // ✅ Return standardized response
      return {
        ...SignupResponseStructure,
        status: 'success',
        ...result.data,
      };
    } else {
      // ✅ Return error response
      return {
        ...SignupResponseStructure,
        status: 'failure',
        message: result.message || 'Signup failed',
      };
    }
  } catch (error) {
    console.error('❌ Signup Error:', error);
    return {
      ...SignupResponseStructure,
      status: 'error',
      securityToken: '',
      userId: '',
      message: error.message || 'Signup failed. Please try again.',
    };
  }
};

/**
 * Validate signup data before sending to API
 * @param {Object} userData - User data to validate
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateSignupData = (userData) => {
  const errors = [];

  // Required fields validation
  if (!userData.name || userData.name.trim() === '') {
    errors.push('Name is required');
  }

  if (!userData.email || !isValidEmail(userData.email)) {
    errors.push('Valid email is required');
  }

  if (!userData.password || userData.password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }

  if (!userData.mobileNo || userData.mobileNo.length !== 10) {
    errors.push('Valid 10-digit mobile number is required');
  }

  if (!userData.dateOfBirth) {
    errors.push('Date of birth is required');
  }

  if (userData.acceptTerms !== 1) {
    errors.push('You must accept the terms and conditions');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Helper: Validate email format
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
